import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth, ProtectedRoute } from '../../components/auth/AuthContext'

interface CookiePreference {
  id: string
  anonymousId: string
  consent: {
    necessary: boolean
    analytics: boolean
    marketing: boolean
  }
  timestamp: string
  userAgent: string
  ipAddress?: string
}

interface CookieStats {
  total: number
  acceptedAll: number
  rejectedAll: number
  custom: number
}

function CookiesContent() {
  const { email, logout } = useAuth()
  const navigate = useNavigate()
  const [preferences, setPreferences] = useState<CookiePreference[]>([])
  const [stats, setStats] = useState<CookieStats | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchPreferences()
  }, [])

  const fetchPreferences = async () => {
    try {
      const response = await fetch('/api/cookie-preferences')
      const data = await response.json()
      setPreferences(data.preferences || [])
      setStats(data.stats)
    } catch (error) {
      console.error('Error fetching preferences:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleLogout = async () => {
    await logout()
    navigate('/admin/login')
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getConsentType = (consent: CookiePreference['consent']) => {
    if (consent.analytics && consent.marketing) {
      return { label: 'Accettati tutti', color: 'bg-green-100 text-green-800' }
    }
    if (!consent.analytics && !consent.marketing) {
      return { label: 'Solo necessari', color: 'bg-red-100 text-red-800' }
    }
    return { label: 'Personalizzato', color: 'bg-blue-100 text-blue-800' }
  }

  const parseUserAgent = (ua: string) => {
    if (!ua) return 'Sconosciuto'
    
    // Simple browser detection
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome'
    if (ua.includes('Firefox')) return 'Firefox'
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari'
    if (ua.includes('Edg')) return 'Edge'
    if (ua.includes('Opera') || ua.includes('OPR')) return 'Opera'
    
    return 'Altro'
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Link to="/admin" className="text-gray-500 hover:text-gray-700">
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </Link>
            <h1 className="text-xl font-bold text-gray-900">Consensi Cookie</h1>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{email}</span>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-red-600 transition-colors"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F8B120]"></div>
            </div>
          ) : (
            <>
              {/* Stats */}
              {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="text-3xl font-bold text-gray-900">{stats.total}</div>
                    <div className="text-sm text-gray-500">Consensi totali</div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="text-3xl font-bold text-green-600">{stats.acceptedAll}</div>
                    <div className="text-sm text-gray-500">Accettati tutti</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {stats.total > 0 ? ((stats.acceptedAll / stats.total) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="text-3xl font-bold text-red-600">{stats.rejectedAll}</div>
                    <div className="text-sm text-gray-500">Solo necessari</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {stats.total > 0 ? ((stats.rejectedAll / stats.total) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                  <div className="bg-white rounded-xl shadow-sm p-6">
                    <div className="text-3xl font-bold text-blue-600">{stats.custom}</div>
                    <div className="text-sm text-gray-500">Personalizzati</div>
                    <div className="text-xs text-gray-400 mt-1">
                      {stats.total > 0 ? ((stats.custom / stats.total) * 100).toFixed(1) : 0}%
                    </div>
                  </div>
                </div>
              )}

              {/* Recent Consents Table */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Ultimi consensi (max 100)
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ID Anonimo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Tipo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Analytics
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Marketing
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Browser
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {preferences.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                            Nessun consenso registrato ancora.
                          </td>
                        </tr>
                      ) : (
                        preferences.map(pref => {
                          const consentType = getConsentType(pref.consent)
                          return (
                            <tr key={pref.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                                {formatDate(pref.timestamp)}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 font-mono">
                                {pref.anonymousId.substring(0, 15)}...
                              </td>
                              <td className="px-6 py-4">
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${consentType.color}`}>
                                  {consentType.label}
                                </span>
                              </td>
                              <td className="px-6 py-4">
                                {pref.consent.analytics ? (
                                  <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                )}
                              </td>
                              <td className="px-6 py-4">
                                {pref.consent.marketing ? (
                                  <svg className="h-5 w-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                ) : (
                                  <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                )}
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500">
                                {parseUserAgent(pref.userAgent)}
                              </td>
                            </tr>
                          )
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* GDPR Info */}
              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="text-lg font-semibold text-blue-900 mb-2">Informazioni GDPR</h4>
                <p className="text-sm text-blue-800">
                  Questi dati vengono raccolti in forma anonima per dimostrare la conformita al GDPR.
                  Gli ID anonimi non permettono di identificare gli utenti. I dati vengono conservati
                  per il periodo previsto dalla normativa vigente.
                </p>
              </div>
            </>
          )}
        </motion.div>
      </main>
    </div>
  )
}

export default function AdminCookies() {
  return (
    <ProtectedRoute>
      <CookiesContent />
    </ProtectedRoute>
  )
}
